import { OrgGuideFileVersionId, typedObjectFromEntries } from "@hex/common";

import { ORG_ID } from "../../../../orgs";
import { selectContextWorkbench } from "../../../../redux/slices/contextWorkbenchSlice";
import { RootState } from "../../../../redux/store";
import { createSelector } from "../../../../redux/utils/index.js";
import { ContextWorkbenchEntity } from "../contextWorkbenchEntity";
import {
  ContextWorkbenchFile,
  ContextWorkbenchFileId,
} from "../contextWorkbenchFile";
import { calculateOrgGuideFileDiffStatus } from "../utils/diff";
import {
  getEditorFilesListNodeForAsyncPlaceholder,
  getEditorFilesListNodeForFile,
} from "../utils/editorFilesListNode";
import { fileComparator, getFileForEntity } from "../utils/file";
import {
  getContextWorkbenchFileNodeGroups,
  getContextWorkbenchFileNodeGroupsAsyncPlaceholder,
} from "../utils/sources";

type SliceState = Pick<RootState, "contextWorkbench">;

const selectPublishFiles = createSelector(
  [(s: SliceState) => selectContextWorkbench(s).publish.changeset],
  (changeset) => {
    if (!changeset.value) {
      return [];
    }

    const files = changeset.value.orgGuideFileChanges
      .map((change) => {
        const file: ContextWorkbenchFile.Entity = getFileForEntity({
          type: ContextWorkbenchEntity.Type.OrgGuideFile,
          contents: change.orgGuideFileVersion?.contentsText ?? null,
          filePath: change.orgGuideFile.filePath,
          orgId: ORG_ID,
          orgGuideFileId: change.orgGuideFile.id,
          orgGuideFileVersionId:
            change.orgGuideFileVersion?.id ??
            OrgGuideFileVersionId.check(`#deleted:${change.orgGuideFile.id}`),
          externalSource: change.orgGuideFileVersion?.externalSource ?? null,
        });
        const diff = calculateOrgGuideFileDiffStatus({
          baseVersion: change.baseOrgGuideFileVersion,
          headVersion: change.orgGuideFileVersion,
        });
        const baseContents = change.baseOrgGuideFileVersion?.contentsText ?? "";
        return {
          ...file,
          diff: { status: diff, baseContents },
          externalSource: change.orgGuideFileVersion?.externalSource ?? null,
        };
      })
      .sort(fileComparator);
    return files;
  },
);

const selectPublishFilesById = createSelector([selectPublishFiles], (files) => {
  return typedObjectFromEntries(files.map((file) => [file.id, file]));
});

const selectSelectedPublishFile = createSelector(
  [
    selectPublishFilesById,
    (s: SliceState) => selectContextWorkbench(s).publish.selectedFileId,
  ],
  (filesById, selectedFileId) => {
    return selectedFileId ? (filesById[selectedFileId] ?? null) : null;
  },
);

const selectPublishFileListNodesBySource = createSelector(
  [
    (s: SliceState) => selectContextWorkbench(s).publish.changeset,
    selectPublishFiles,
  ],
  (changeset, files) => {
    if (!changeset.value) {
      const node = getEditorFilesListNodeForAsyncPlaceholder(
        ContextWorkbenchEntity.Type.OrgGuideFile,
        changeset.loading,
        changeset.error,
      );
      return getContextWorkbenchFileNodeGroupsAsyncPlaceholder(node);
    } else {
      const nodes = files.map((file) => ({
        ...getEditorFilesListNodeForFile(file.id),
        externalSource: file.externalSource,
      }));

      return getContextWorkbenchFileNodeGroups(nodes);
    }
  },
);

export const contextWorkbenchPublishSelectors = {
  selectIsPublishing: (s: SliceState) =>
    selectContextWorkbench(s).view === "publish",
  selectView: (s: SliceState) => selectContextWorkbench(s).publish.view,
  selectChangeset: (s: SliceState) =>
    selectContextWorkbench(s).publish.changeset,
  selectLoadedChangeset: (s: SliceState) =>
    contextWorkbenchPublishSelectors.selectChangeset(s).value,
  selectAskPreviewThread: (s: SliceState) =>
    selectContextWorkbench(s).publish.askPreviewThread,
  selectFiles: selectPublishFiles,
  selectFilesById: selectPublishFilesById,
  selectFileById: (fileId: ContextWorkbenchFileId) => (s: SliceState) =>
    selectPublishFilesById(s)[fileId],
  selectSelectedFile: selectSelectedPublishFile,
  selectFileListNodesBySource: selectPublishFileListNodesBySource,
};
